/**
 * Jovii — HOW MUCH TO ZOOM A TRAILER SO ITS BLACK BARS FALL OUTSIDE.
 *
 * A trailer wider than 16:9 ships its letterbox burned into the video
 * (docs/TRAILERS.md § E4 — the IFrame API has no dimensions and the frame is
 * cross-origin, so nothing can be measured out of the embed itself). What CAN
 * be measured is the video's own frames: `i.ytimg.com/vi/<id>/mq{1,2,3}.jpg`
 * are three auto-extracted 320×180 frames of the actual video, served with
 * `access-control-allow-origin: *` (probed 2026-08-17), so a crossorigin
 * canvas may read their pixels. A burned-in bar is black in EVERY frame; a
 * dark scene is black in one — `cropVerdict` in trailer-rules.ts takes the
 * minimum across frames and refuses everything uncertain. Its answer is a
 * linear zoom, capped at 15%, that the stage applies as a transform inside its
 * overflow-hidden box (and the echo multiplies into its own scale, so the two
 * layers agree about where the picture is).
 *
 * `mqdefault` is deliberately NOT sampled: it is the uploader's chosen
 * thumbnail — full-bleed art, no bars, tells us nothing about the video.
 * oEmbed's width/height were probed and rejected: 200×113 for a 2.39:1
 * trailer, because oEmbed reports the 16:9 raster, never the burned-in bars.
 *
 * COST: three ~4–12 KB JPEGs, fetched once per video during the still hold
 * (the answer lands in well under a second against a ~5.7 s reveal), then
 * remembered — per session in a Map, across visits in localStorage. Failure
 * of any kind — a 404 frame, a canvas a browser taints anyway, storage that
 * throws — answers 1 and stays quiet: an honest bar is a cosmetic defect, and
 * this module must never be the reason a trailer looks wrong.
 */

import { BAR_LUMA, CROP_MIN_SCANS, barDepths, cropVerdict, type BarScan } from './trailer-rules';

/** The three auto-extracted frames. 320×180, the video's own raster. */
const FRAMES = ['mq1', 'mq2', 'mq3'] as const;

const STORE_KEY = 'jt.trailerCrop.v1';

/** Session memory. Promises, so ten mounts of one id fetch once. */
const mem = new Map<string, Promise<number>>();

function fromStore(ytId: string): number | null {
  try {
    const raw = localStorage.getItem(`${STORE_KEY}.${ytId}`);
    if (raw === null) return null;
    const n = Number(raw);
    // Anything outside the rules' own range is a stale or foreign write.
    return Number.isFinite(n) && n >= 1 && n <= 1.5 ? n : null;
  } catch {
    return null;
  }
}

function toStore(ytId: string, scale: number): void {
  try {
    localStorage.setItem(`${STORE_KEY}.${ytId}`, scale.toFixed(4));
  } catch {
    /* private mode: the Map still remembers for this session */
  }
}

function loadFrame(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // What makes the canvas readable afterwards; ytimg sends `ACAO: *`.
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`no frame at ${url}`));
    img.src = url;
  });
}

/**
 * One frame's edge runs. Luminance is max(r,g,b) — stricter than a weighted
 * grey, which is right for "is this pixel actually black": a deep-blue glow
 * that a rec.601 mix would call dark is still picture, not bar.
 */
function scanFrame(img: HTMLImageElement, ctx: CanvasRenderingContext2D): BarScan | null {
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (w < 32 || h < 32) return null;
  ctx.canvas.width = w;
  ctx.canvas.height = h;
  ctx.drawImage(img, 0, 0);
  const data = ctx.getImageData(0, 0, w, h).data;
  const rowMax = new Uint8Array(h);
  const colMax = new Uint8Array(w);
  for (let y = 0; y < h; y += 1) {
    const row = y * w * 4;
    for (let x = 0; x < w; x += 1) {
      const i = row + x * 4;
      const r = data[i]!;
      const g = data[i + 1]!;
      const b = data[i + 2]!;
      const v = r > g ? (r > b ? r : b) : g > b ? g : b;
      if (v > rowMax[y]!) rowMax[y] = v;
      if (v > colMax[x]!) colMax[x] = v;
    }
  }
  const rows = barDepths(rowMax, BAR_LUMA);
  const cols = barDepths(colMax, BAR_LUMA);
  return { width: w, height: h, top: rows.lead, bottom: rows.trail, left: cols.lead, right: cols.trail };
}

async function measure(ytId: string): Promise<number> {
  const canvas = document.createElement('canvas');
  // `willReadFrequently` keeps the three readbacks off the GPU round-trip.
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (ctx === null) return 1;
  const settled = await Promise.allSettled(
    FRAMES.map((f) => loadFrame(`https://i.ytimg.com/vi/${ytId}/${f}.jpg`))
  );
  const scans: BarScan[] = [];
  for (const s of settled) {
    if (s.status !== 'fulfilled') continue;
    try {
      const scan = scanFrame(s.value, ctx);
      if (scan !== null) scans.push(scan);
    } catch {
      // A tainted canvas (a proxy stripped the CORS header) reads as "unknown".
    }
  }
  if (scans.length < CROP_MIN_SCANS) return 1;
  return cropVerdict(scans).scale;
}

/**
 * The zoom for this video, from cache or from its frames. Never rejects;
 * every failure is the same honest answer as "no bars found": 1.
 */
export function cropFactorFor(ytId: string): Promise<number> {
  if (typeof document === 'undefined') return Promise.resolve(1);
  const held = mem.get(ytId);
  if (held !== undefined) return held;
  const stored = fromStore(ytId);
  if (stored !== null) {
    const done = Promise.resolve(stored);
    mem.set(ytId, done);
    return done;
  }
  const job = measure(ytId)
    .then((scale) => {
      toStore(ytId, scale);
      return scale;
    })
    .catch(() => 1);
  mem.set(ytId, job);
  return job;
}

/** Tests and the debug harness only. */
export function resetCropCacheForTests(): void {
  mem.clear();
}
